<script lang="ts">
	import { onMount } from 'svelte';
	import { organizationsApi, countriesApi } from '../api';
	import type { Organization, OrganizationCreate, OrganizationUpdate, Country } from '../api';
	import Icon from './Icon.svelte';

	type Props = {
		isOpen: boolean;
		organization?: Organization | null;
		onClose: () => void;
		onSaved: () => void;
	};

	const { isOpen, organization = null, onClose, onSaved }: Props = $props();

	// Determine if we're editing or creating
	const isEditMode = $derived(!!organization);

	let formData = $state<OrganizationCreate>({
		name: '',
		countryId: ''
	});

	let countries = $state<Country[]>([]);
	let loading = $state(false);
	let error = $state<string | null>(null);
	let formErrors = $state<Record<string, string>>({});

	// Populate form when organization changes (for edit mode)
	$effect(() => {
		if (organization) {
			formData = {
				name: organization.name || '',
				countryId: organization.countryId || organization.country?.id || ''
			};
		} else {
			// Reset form for create mode
			formData = {
				name: '',
				countryId: ''
			};
		}
		// Clear errors when switching modes
		formErrors = {};
		error = null;
	});

	function handleBackdropClick(event: MouseEvent) {
		if (event.target === event.currentTarget) {
			onClose();
		}
	}

	function validateForm(): boolean {
		const errors: Record<string, string> = {};

		if (!formData.name.trim()) {
			errors.name = 'Organization name is required';
		} else if (formData.name.trim().length < 2) {
			errors.name = 'Organization name must be at least 2 characters';
		}

		if (!formData.countryId) {
			errors.countryId = 'Please select a country';
		}

		formErrors = errors;
		return Object.keys(errors).length === 0;
	}

	async function handleSubmit(event: Event) {
		event.preventDefault();

		if (!validateForm()) {
			return;
		}

		try {
			loading = true;
			error = null;

			if (isEditMode && organization) {
				// Update existing organization
				const updateData: OrganizationUpdate = {
					name: formData.name.trim(),
					countryId: formData.countryId
				};
				await organizationsApi.updateOrganization(organization.id, updateData);
			} else {
				// Create new organization
				const createData: OrganizationCreate = {
					name: formData.name.trim(),
					countryId: formData.countryId
				};
				await organizationsApi.createOrganization(createData);
			}

			onSaved();
		} catch (err) {
			console.error('Error saving organization:', err);
			error = err instanceof Error ? err.message : 'Failed to save organization';
		} finally {
			loading = false;
		}
	}

	async function loadCountries() {
		try {
			countries = await countriesApi.getCountries();
		} catch (err) {
			console.error('Error loading countries:', err);
			error = 'Failed to load countries';
		}
	}

	onMount(() => {
		loadCountries();
	});
</script>

<!-- Drawer Overlay -->
{#if isOpen}
	<div
		class="fixed inset-0 z-50 overflow-hidden"
		aria-labelledby="slide-over-title"
		role="dialog"
		aria-modal="true"
	>
		<div class="absolute inset-0 overflow-hidden">
			<!-- Background overlay -->
			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<!-- svelte-ignore a11y_no_static_element_interactions -->
			<div
				class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
				onclick={handleBackdropClick}
			></div>

			<!-- Drawer container -->
			<div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
				<div class="pointer-events-auto relative w-screen max-w-md">
					<!-- Close button -->
					<div class="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4">
						<button
							type="button"
							class="relative rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
							onclick={onClose}
						>
							<span class="absolute -inset-2.5"></span>
							<span class="sr-only">Close panel</span>
							<Icon name="x" size={24} />
						</button>
					</div>

					<!-- Drawer content -->
					<div class="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
						<div class="px-4 sm:px-6">
							<h2 class="text-base font-semibold leading-6 text-gray-900" id="slide-over-title">
								{isEditMode ? 'Edit Organization' : 'Create New Organization'}
							</h2>
						</div>

						<div class="relative mt-6 flex-1 px-4 sm:px-6">
							<!-- Form -->
							<form onsubmit={handleSubmit} class="space-y-6">
								<!-- Global Error -->
								{#if error}
									<div class="rounded-md bg-red-50 p-4">
										<div class="text-sm text-red-700">{error}</div>
									</div>
								{/if}

								<!-- Organization Name -->
								<div>
									<label for="name" class="block text-sm font-medium leading-6 text-gray-900">
										Organization Name <span class="text-red-500">*</span>
									</label>
									<div class="mt-2">
										<input
											type="text"
											id="name"
											bind:value={formData.name}
											class="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 {formErrors.name
												? 'ring-red-300 focus:ring-red-500'
												: ''}"
											placeholder="e.g., Harbor Sailing Club"
											required
										/>
										{#if formErrors.name}
											<p class="mt-2 text-sm text-red-600">{formErrors.name}</p>
										{/if}
									</div>
								</div>

								<!-- Country Selection -->
								<div>
									<label for="countryId" class="block text-sm font-medium leading-6 text-gray-900">
										Country <span class="text-red-500">*</span>
									</label>
									<div class="mt-2">
										<select
											id="countryId"
											bind:value={formData.countryId}
											class="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 {formErrors.countryId
												? 'ring-red-300 focus:ring-red-500'
												: ''}"
											required
										>
											<option value="">Select a country</option>
											{#each countries as country (country.id)}
												<option value={country.id}>{country.isoName}</option>
											{/each}
										</select>
										{#if formErrors.countryId}
											<p class="mt-2 text-sm text-red-600">{formErrors.countryId}</p>
										{/if}
									</div>
								</div>

								<!-- Form Actions -->
								<div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
									<button
										type="button"
										onclick={onClose}
										class="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
										disabled={loading}
									>
										Cancel
									</button>
									<button
										type="submit"
										class="inline-flex justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
										disabled={loading}
									>
										{#if loading}
											<svg
												class="-ml-1 mr-3 h-4 w-4 animate-spin text-white"
												fill="none"
												viewBox="0 0 24 24"
											>
												<circle
													class="opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													stroke-width="4"
												></circle>
												<path
													class="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												></path>
											</svg>
											{isEditMode ? 'Updating...' : 'Creating...'}
										{:else}
											{isEditMode ? 'Update Organization' : 'Create Organization'}
										{/if}
									</button>
								</div>
							</form>

							<!-- Additional Info for New Users -->
							{#if !isEditMode}
								<div class="mt-8 rounded-lg bg-blue-50 p-4">
									<div class="flex">
										<div class="ml-3">
											<h3 class="text-sm font-medium text-blue-800">Organization Ownership</h3>
											<div class="mt-2 text-sm text-blue-700">
												<p>As the creator, you will be the owner of this organization and can:</p>
												<ul class="mt-1 list-inside list-disc space-y-1">
													<li>Edit organization details</li>
													<li>Invite and manage members</li>
													<li>Assign roles to members</li>
													<li>Delete the organization</li>
												</ul>
											</div>
										</div>
									</div>
								</div>
							{/if}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
{/if}
